// Feste Auswahllisten der Verkaufsgespräch-Erfassung (crm.sales_talks).
// Die Werte werden 1:1 in der Datenbank gespeichert — Wortlaut nicht ändern,
// sonst passen Bestandsdaten und Auswertungen nicht mehr zusammen.

export const CONTRACT_OPTIONS = [
  '6 Monate 1x',
  '6 Monate Xx',
  '12 Monate 1x',
  '12 Monate Xx',
  '24 Monate 1x',
  '24 Monate Xx',
  'Kein Abschluss',
] as const;

export const NO_CONTRACT = 'Kein Abschluss';

// Wortlaut nicht ändern: Die Werte stehen genauso in der Datenbank
// (crm.sales_talks.no_contract_reason_category, dort mit einer Prüfregel
// abgesichert) und CRM wie Verkaufs-App nutzen dieselbe Liste. Erweitert
// im August 2026, weil 26 von 42 Nicht-Abschlüssen im Freitext gelandet
// waren und sich davon nichts zählen ließ.
export const NO_CONTRACT_REASONS = [
  'zu teuer',
  'muss sich mit Partner absprechen',
  'Bedenkzeit gewünscht',
  'kommt später wieder (Termin vereinbart)',
  'Kind hat nicht mittrainiert',
  'gefällt das Training nicht',
  'Kurse zu voll',
  'Zeiten passen nicht',
  'kein Verkaufsgespräch möglich',
  'gesundheitliche Gründe',
  'Kurs abgebrochen',
  'Sonstiges',
] as const;

export const REASON_OTHER = 'Sonstiges';

/** Nur die echten Laufzeiten — ohne „Kein Abschluss". */
export const CONTRACT_TYPE_OPTIONS = CONTRACT_OPTIONS.filter(o => o !== NO_CONTRACT);

// ============================================================
// Vier Ergebnisse statt Auswahl „Kein Abschluss"
//
// Seit September 2026 unterscheiden CRM und Verkaufs-App beim
// Verkaufsgespräch ausdrücklich: Abschluss / Kein Abschluss / Kein Gespräch /
// Kurs abgebrochen. Wer gar nicht zum Gespräch kam — Kind hat nicht
// mittrainiert, Eltern nach dem Training sofort weg, Probetraining
// abgebrochen — landete vorher unter „Kein Abschluss" und zog die Quote nach
// unten, obwohl niemand etwas verkaufen konnte.
//
// Gespeichert wird weiterhin „Kein Abschluss" plus Kategorie; die Kategorien
// unten entscheiden, was als „ohne Gespräch" gilt. Wortlaut identisch zu CRM
// und Verkaufs-App.
// ============================================================

export const KURS_ABGEBROCHEN = 'Kurs abgebrochen';

/** Die Gründe, die unter „Kein Gespräch" zur Wahl stehen. */
export const KEIN_GESPRAECH_GRUENDE = [
  'kein Verkaufsgespräch möglich',
  'Kind hat nicht mittrainiert',
] as const;

/** Alle Kategorien, bei denen kein Verkaufsgespräch stattgefunden hat. */
export const OHNE_GESPRAECH_KATEGORIEN: readonly string[] = [...KEIN_GESPRAECH_GRUENDE, KURS_ABGEBROCHEN];

/** Die Gründe für ein echtes „Kein Abschluss" — Gespräch geführt, kein Vertrag. */
export const KEIN_ABSCHLUSS_GRUENDE = NO_CONTRACT_REASONS.filter(r => !OHNE_GESPRAECH_KATEGORIEN.includes(r));

/** Ohne Gespräch ist der Freitext Pflicht — so lang wie bei „Status unbekannt" in der Verkaufs-App. */
export const OHNE_GESPRAECH_MIN_ZEICHEN = 10;

export type Ergebnis = 'abschluss' | 'kein_abschluss' | 'kein_gespraech' | 'kurs_abgebrochen';

export const ERGEBNIS_OPTIONEN: ReadonlyArray<{ wert: Ergebnis; label: string; hinweis: string }> = [
  { wert: 'abschluss',        label: 'Abschluss',        hinweis: 'Vertrag abgeschlossen' },
  { wert: 'kein_abschluss',   label: 'Kein Abschluss',   hinweis: 'Gespräch geführt, aber kein Vertrag' },
  { wert: 'kein_gespraech',   label: 'Kein Gespräch',    hinweis: 'Person war da, ein Gespräch kam nicht zustande' },
  { wert: 'kurs_abgebrochen', label: 'Kurs abgebrochen', hinweis: 'Probetraining vorzeitig verlassen' },
];

export const ERGEBNIS_LABEL: Record<Ergebnis, string> =
  Object.fromEntries(ERGEBNIS_OPTIONEN.map(o => [o.wert, o.label])) as Record<Ergebnis, string>;

/** Die gezählte Kategorie — bei Altfällen ohne Kategorie aus dem Freitext, sofern der wörtlich passt. */
export const kategorieVon = (t: Pick<SalesTalkRow, 'no_contract_reason' | 'no_contract_reason_category'> | null | undefined): string => {
  if (!t) return '';
  if (t.no_contract_reason_category) return t.no_contract_reason_category;
  const text = t.no_contract_reason ?? '';
  return (NO_CONTRACT_REASONS as readonly string[]).includes(text) ? text : (text ? REASON_OTHER : '');
};

/** Welches der vier Ergebnisse steckt in einem gespeicherten Gespräch? Ohne Ergebnis: leer („noch offen"). */
export const ergebnisVon = (t: Pick<SalesTalkRow, 'signed_contract' | 'no_contract_reason' | 'no_contract_reason_category'> | null | undefined): Ergebnis | '' => {
  if (!t?.signed_contract) return '';
  if (t.signed_contract !== NO_CONTRACT) return 'abschluss';
  const kat = kategorieVon(t);
  if (kat === KURS_ABGEBROCHEN) return 'kurs_abgebrochen';
  if ((KEIN_GESPRAECH_GRUENDE as readonly string[]).includes(kat)) return 'kein_gespraech';
  return 'kein_abschluss';
};

/** Erschienen, aber kein Verkaufsgespräch — zählt in der Quote weiter als Nicht-Abschluss, wird aber eigens ausgewiesen. */
export const istOhneGespraech = (e: Ergebnis | ''): boolean => e === 'kein_gespraech' || e === 'kurs_abgebrochen';

// Verkäufer je Sportschule (crm.sales_reps). Bewusst eine gepflegte Liste
// statt des Personalverzeichnisses: Am Tresen läuft meist der geteilte
// Studio-Account, aus der Anmeldung ist der Verkäufer nicht ableitbar — der
// gewählte Name ist die einzige verlässliche Quelle. Das Personalverzeichnis
// enthielt dafür zu viel und zeigte Mitarbeiter ohne Standort-Zuordnung
// sogar an jeder Sportschule.
export interface SalesRepOption {
  id: string;
  school_id: string;
  name: string;
  /** Sammeleintrag wie „Vertretung" — verlangt beim Erfassen zusätzlich einen Namen. */
  braucht_namen: boolean;
}

// Springt jemand ein, der nicht auf der Liste des Standorts steht, wird
// „Vertretung" gewählt und der Name dazugeschrieben. Gespeichert wird er im
// Namensschnappschuss („Vertretung: Anna Beispiel") — dadurch erscheint in
// der Auswertung die Person und nicht die Rolle. Ohne Namen lehnt die
// Datenbank das Speichern ab; das Formular fragt ihn vorher ab.

/** Was als Verkäufername gespeichert wird. */
export const verkaeuferSchnappschuss = (
  rep: { name: string; braucht_namen?: boolean } | undefined,
  vertretungName: string,
): string | null => {
  if (!rep) return null;
  return rep.braucht_namen ? `${rep.name}: ${vertretungName.trim()}` : rep.name;
};

/** Den eingesprungenen Namen aus einem gespeicherten Schnappschuss zurückholen. */
export const vertretungAusSchnappschuss = (
  rep: { name: string; braucht_namen?: boolean } | undefined,
  schnappschuss: string | null | undefined,
): string => {
  if (!rep?.braucht_namen || !schnappschuss) return '';
  const praefix = `${rep.name}: `;
  return schnappschuss.startsWith(praefix) ? schnappschuss.slice(praefix.length) : '';
};

// Zeile aus crm.sales_talks (nur die im Frontend genutzten Felder).
export interface SalesTalkRow {
  id: string;
  trial_booking_id: string;
  customer_id: string | null;
  school_id: string | null;
  sales_staff_id: string | null;
  sales_staff_name: string | null;
  /** Verweis auf die fest hinterlegte Verkäuferliste (crm.sales_reps). */
  sales_rep_id: string | null;
  referred_by: string | null;
  signed_contract: string | null;
  /** Freitext zum Nicht-Abschluss — das, was im Gespräch gesagt wurde. */
  no_contract_reason: string | null;
  /** Feste Kategorie daneben, damit sich die Gründe zählen lassen. */
  no_contract_reason_category: string | null;
}
